import { apiFetch } from "../api/client";
import { tenantPath } from "../utils/tenantContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import API from "../config";

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

function riskClass(level) {
  return `identity-risk identity-risk-${String(
    level || "low"
  ).toLowerCase()}`;
}

function postureClass(status) {
  return `identity-posture identity-posture-${String(
    status || "unknown"
  ).replaceAll("_", "-")}`;
}

function decisionClass(decision) {
  return `identity-decision identity-decision-${String(
    decision || "unknown"
  ).replaceAll("_", "-")}`;
}


const parsePostureEvidence = (posture) => {
  const raw = posture?.evidence;

  if (!raw) {
    return {};
  }

  if (typeof raw === "object") {
    return raw;
  }

  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn(
        "Unable to parse posture evidence:",
        error
      );
    }
  }

  return {};
};

const postureEvidenceCoverage = (posture) => {
  const evidence =
    parsePostureEvidence(posture);

  const coverage =
    Number(
      evidence?.coverage_percent
    );

  return Number.isFinite(coverage)
    ? coverage
    : 0;
};

const postureSignalState = (
  posture,
  signalName
) => {
  const evidence =
    parsePostureEvidence(posture);

  return (
    evidence?.signals?.[signalName]
      ?.state ||
    "unknown"
  );
};

const postureSignalLabel = (
  posture,
  signalName,
  positiveLabel,
  negativeLabel
) => {
  const state =
    postureSignalState(
      posture,
      signalName
    );

  if (state === "true") {
    return positiveLabel;
  }

  if (state === "false") {
    return negativeLabel;
  }

  return "Unknown";
};

const postureNumericSignalLabel = (
  posture,
  signalName,
  value
) => {
  const state =
    postureSignalState(
      posture,
      signalName
    );

  if (state === "unknown") {
    return "Unknown";
  }

  if (
    value === null ||
    value === undefined
  ) {
    return "Unknown";
  }

  return value;
};

const postureAssessmentLabel = (posture) => {
  const coverage =
    postureEvidenceCoverage(posture);

  if (
    posture?.posture_status === "unknown"
    || coverage < 60
  ) {
    return "Unknown — Insufficient Evidence Coverage";
  }

  return String(
    posture?.posture_status ||
    "unknown"
  )
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const unknownPostureSignalCount = (posture) => {
  const evidence =
    parsePostureEvidence(posture);

  return Object.values(
    evidence?.signals || {}
  ).filter(
    (signal) =>
      signal?.state === "unknown"
  ).length;
};

function IdentityZeroTrustPage() {
  const [postureDeviceUuid, setPostureDeviceUuid] =
    useState("");
  const [evaluatingPosture, setEvaluatingPosture] =
    useState(false);
  const [postureResult, setPostureResult] =
    useState(null);

  const [accessDecisionForm, setAccessDecisionForm] =
    useState({
      identity_id: "",
      device_uuid: "",
      resource_type: "application",
      resource_identifier: "",
      requested_action: "access",
      session_risk_score: 0,
    });

  const [evaluatingAccess, setEvaluatingAccess] =
    useState(false);
  const [accessDecisionResult, setAccessDecisionResult] =
    useState(null);

  const [identityCreateForm, setIdentityCreateForm] =
    useState({
      username: "",
      display_name: "",
      email: "",
      identity_type: "workforce",
      status: "active",
      privileged: false,
      service_account: false,
      break_glass: false,
      mfa_enabled: false,
      passwordless_enabled: false,
    });

  const [creatingIdentity, setCreatingIdentity] =
    useState(false);

  const [identityCreateResult, setIdentityCreateResult] =
    useState(null);

  const [roleAssignmentForm, setRoleAssignmentForm] =
    useState({
      identity_id: "",
      role_id: "",
      assignment_type: "permanent",
    });

  const [assigningRole, setAssigningRole] =
    useState(false);

  const [roleAssignmentResult, setRoleAssignmentResult] =
    useState(null);

  const [pamCreateForm, setPamCreateForm] =
    useState({
      identity_id: "",
      role_id: "",
      business_justification: "",
      requested_duration_minutes: "60",
      resource_type: "",
      resource_identifier: "",
    });

  const [creatingPamRequest, setCreatingPamRequest] =
    useState(false);

  const [pamCreateResult, setPamCreateResult] =
    useState(null);

  const [summary, setSummary] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [roles, setRoles] = useState([]);
  const [postures, setPostures] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [accessReviews, setAccessReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [updatingReviewItemId, setUpdatingReviewItemId] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingRequestId, setUpdatingRequestId] =
    useState(null);
  const [error, setError] = useState("");

  const loadIdentityData = useCallback(
    async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const [
          summaryResponse,
          identitiesResponse,
          requestsResponse,
          rolesResponse,
          accessReviewsResponse,
        ] = await Promise.all([
          apiFetch(tenantPath(`/identity/summary`)),
          apiFetch(tenantPath(`/identity/identities?limit=100`)),
          apiFetch(tenantPath(`/identity/privileged-access?limit=100`)),
          apiFetch(tenantPath(`/identity/roles`)),
          apiFetch(tenantPath(`/identity/access-reviews?limit=100`)),
        ]);

        const summaryData = await summaryResponse.json();
        const identitiesData =
          await identitiesResponse.json();
        const requestsData =
          await requestsResponse.json();
        const rolesData = await rolesResponse.json();
        const accessReviewsData =
          await accessReviewsResponse.json();

        if (!summaryResponse.ok) {
          throw new Error(
            summaryData.detail ||
              "Unable to load identity summary."
          );
        }

        if (!identitiesResponse.ok) {
          throw new Error(
            identitiesData.detail ||
              "Unable to load identities."
          );
        }

        if (!requestsResponse.ok) {
          throw new Error(
            requestsData.detail ||
              "Unable to load privileged-access requests."
          );
        }

        if (!rolesResponse.ok) {
          throw new Error(
            rolesData.detail ||
              "Unable to load identity roles."
          );
        }

        if (!accessReviewsResponse.ok) {
          throw new Error(
            accessReviewsData.detail ||
              "Unable to load access reviews."
          );
        }

        setSummary(summaryData);

        setIdentities(
          Array.isArray(identitiesData)
            ? identitiesData
            : []
        );

        setRequests(
          Array.isArray(requestsData)
            ? requestsData
            : []
        );

        setRoles(
          Array.isArray(rolesData)
            ? rolesData
            : []
        );

        setAccessReviews(
          Array.isArray(accessReviewsData)
            ? accessReviewsData
            : []
        );

        const postureResponse = await apiFetch(tenantPath(`/identity/device-posture`)
        );

        if (postureResponse.ok) {
          const postureData =
            await postureResponse.json();

          setPostures(
            Array.isArray(postureData)
              ? postureData
              : []
          );
        } else {
          setPostures([]);
        }

        const decisionResponse = await apiFetch(tenantPath(`/identity/access-decisions?limit=100`)
        );

        if (decisionResponse.ok) {
          const decisionData =
            await decisionResponse.json();

          setDecisions(
            Array.isArray(decisionData)
              ? decisionData
              : []
          );
        } else {
          setDecisions([]);
        }
      } catch (loadError) {
        console.error(
          "Identity dashboard load error:",
          loadError
        );

        setError(loadError.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadIdentityData();
  }, [loadIdentityData]);

  useEffect(() => {
    const handleTenantChange = () => {
      loadIdentityData();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      handleTenantChange
    );

    return () => {
      window.removeEventListener(
        "wirelesspress-tenant-change",
        handleTenantChange
      );
    };
  }, [loadIdentityData]);

  const createIdentity = async () => {
    const username =
      identityCreateForm.username.trim();

    if (!username) {
      window.alert("Username is required.");
      return;
    }

    try {
      setCreatingIdentity(true);
      setIdentityCreateResult(null);

      const payload = {
        username,
        display_name:
          identityCreateForm.display_name.trim() ||
          null,
        email:
          identityCreateForm.email.trim() ||
          null,
        identity_type:
          identityCreateForm.identity_type,
        status:
          identityCreateForm.status,
        privileged:
          Boolean(identityCreateForm.privileged),
        service_account:
          Boolean(identityCreateForm.service_account),
        break_glass:
          Boolean(identityCreateForm.break_glass),
        mfa_enabled:
          Boolean(identityCreateForm.mfa_enabled),
        passwordless_enabled:
          Boolean(
            identityCreateForm.passwordless_enabled
          ),
      };

      const response = await apiFetch(
        tenantPath(`/identity/identities`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to create identity."
        );
      }

      setIdentityCreateResult(data);

      setIdentityCreateForm({
        username: "",
        display_name: "",
        email: "",
        identity_type: "workforce",
        status: "active",
        privileged: false,
        service_account: false,
        break_glass: false,
        mfa_enabled: false,
        passwordless_enabled: false,
      });

      await loadIdentityData(true);
    } catch (createError) {
      console.error(
        "Identity creation error:",
        createError
      );

      window.alert(createError.message);
    } finally {
      setCreatingIdentity(false);
    }
  };

  const assignIdentityRole = async () => {
    const identityId = Number(
      roleAssignmentForm.identity_id
    );

    const roleId = Number(
      roleAssignmentForm.role_id
    );

    if (!identityId) {
      window.alert("Select an identity.");
      return;
    }

    if (!roleId) {
      window.alert("Select a role.");
      return;
    }

    try {
      setAssigningRole(true);
      setRoleAssignmentResult(null);

      const response = await apiFetch(
        tenantPath(
          `/identity/identities/${identityId}/roles`
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role_id: roleId,
            assignment_type:
              roleAssignmentForm.assignment_type,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to assign identity role."
        );
      }

      setRoleAssignmentResult(data);

      setRoleAssignmentForm({
        identity_id: "",
        role_id: "",
        assignment_type: "permanent",
      });

      await loadIdentityData(true);
    } catch (assignmentError) {
      console.error(
        "Identity role assignment error:",
        assignmentError
      );

      window.alert(assignmentError.message);
    } finally {
      setAssigningRole(false);
    }
  };

  const evaluateManagedDevicePosture = async () => {
    const deviceUuid = postureDeviceUuid.trim();

    if (!deviceUuid) {
      window.alert("Enter a managed device UUID.");
      return;
    }

    try {
      setEvaluatingPosture(true);
      setPostureResult(null);

      const response = await apiFetch(
        tenantPath(
          `/identity/device-posture/evaluate/${encodeURIComponent(
            deviceUuid
          )}`
        ),
        {
          method: "POST",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to evaluate managed-device posture."
        );
      }

      setPostureResult(data);

      setAccessDecisionForm((current) => ({
        ...current,
        device_uuid: data.device_uuid || deviceUuid,
      }));

      await loadIdentityData(true);
    } catch (postureError) {
      console.error(
        "Device posture evaluation error:",
        postureError
      );

      window.alert(postureError.message);
    } finally {
      setEvaluatingPosture(false);
    }
  };

  const evaluateAccessDecision = async () => {
    if (!accessDecisionForm.identity_id) {
      window.alert("Select an identity.");
      return;
    }

    if (!accessDecisionForm.resource_type.trim()) {
      window.alert("Resource type is required.");
      return;
    }

    if (!accessDecisionForm.requested_action.trim()) {
      window.alert("Requested action is required.");
      return;
    }

    try {
      setEvaluatingAccess(true);
      setAccessDecisionResult(null);

      const payload = {
        identity_id: Number(
          accessDecisionForm.identity_id
        ),
        resource_type:
          accessDecisionForm.resource_type.trim(),
        resource_identifier:
          accessDecisionForm.resource_identifier.trim() ||
          null,
        requested_action:
          accessDecisionForm.requested_action.trim(),
        session_risk_score: Number(
          accessDecisionForm.session_risk_score || 0
        ),
      };

      if (accessDecisionForm.device_uuid.trim()) {
        payload.device_uuid =
          accessDecisionForm.device_uuid.trim();
      }

      const response = await apiFetch(
        tenantPath(`/identity/access-decisions`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to evaluate access decision."
        );
      }

      setAccessDecisionResult(data);
      await loadIdentityData(true);
    } catch (accessError) {
      console.error(
        "Access decision evaluation error:",
        accessError
      );

      window.alert(accessError.message);
    } finally {
      setEvaluatingAccess(false);
    }
  };

  const createPrivilegedAccessRequest = async () => {
    const identityId = Number(
      pamCreateForm.identity_id
    );

    const roleId = Number(
      pamCreateForm.role_id
    );

    const justification =
      pamCreateForm.business_justification.trim();

    const duration = Number(
      pamCreateForm.requested_duration_minutes
    );

    if (!identityId) {
      window.alert("Select an identity.");
      return;
    }

    if (!roleId) {
      window.alert("Select a privileged role.");
      return;
    }

    if (!justification) {
      window.alert(
        "Business justification is required."
      );
      return;
    }

    if (
      !Number.isFinite(duration) ||
      duration < 5
    ) {
      window.alert(
        "Requested duration must be at least 5 minutes."
      );
      return;
    }

    const selectedRole = roles.find(
      (role) =>
        Number(role.id) === roleId
    );

    const maximumSessionMinutes =
      selectedRole?.maximum_session_minutes == null
        ? null
        : Number(
            selectedRole.maximum_session_minutes
          );

    if (
      maximumSessionMinutes !== null &&
      Number.isFinite(maximumSessionMinutes) &&
      duration > maximumSessionMinutes
    ) {
      window.alert(
        `Maximum duration for this role is ${maximumSessionMinutes} minutes.`
      );
      return;
    }

    try {
      setCreatingPamRequest(true);
      setPamCreateResult(null);

      const payload = {
        identity_id: identityId,
        role_id: roleId,
        business_justification:
          justification,
        requested_duration_minutes:
          duration,
        resource_type:
          pamCreateForm.resource_type.trim() ||
          null,
        resource_identifier:
          pamCreateForm.resource_identifier.trim() ||
          null,
        requested_permissions: [],
      };

      const response = await apiFetch(
        tenantPath(
          "/identity/privileged-access"
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to create privileged-access request."
        );
      }

      setPamCreateResult(data);

      setPamCreateForm({
        identity_id: "",
        role_id: "",
        business_justification: "",
        requested_duration_minutes: "60",
        resource_type: "",
        resource_identifier: "",
      });

      await loadIdentityData(true);
    } catch (pamError) {
      console.error(
        "Privileged-access creation error:",
        pamError
      );

      window.alert(pamError.message);
    } finally {
      setCreatingPamRequest(false);
    }
  };

  const openAccessReview = async (reviewId) => {
    try {
      const response = await apiFetch(tenantPath(`/identity/access-reviews/${reviewId}`)
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Unable to load access review."
        );
      }

      setSelectedReview(data);
    } catch (reviewError) {
      console.error(
        "Access review load error:",
        reviewError
      );

      window.alert(reviewError.message);
    }
  };

  const updateAccessReviewItem = async (
    reviewId,
    itemId,
    decision
  ) => {
    try {
      setUpdatingReviewItemId(itemId);

      const response = await apiFetch(tenantPath(`/identity/access-reviews/${reviewId}/items/${itemId}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            decision,
            decision_reason:
              decision === "approved"
                ? "Access remains required and appropriate."
                : "Access is no longer required.",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to update access review item."
        );
      }

      await loadIdentityData(true);
      await openAccessReview(reviewId);
    } catch (reviewError) {
      console.error(
        "Access review decision error:",
        reviewError
      );

      window.alert(reviewError.message);
    } finally {
      setUpdatingReviewItemId(null);
    }
  };

  const updateRequest = async (
    requestId,
    status
  ) => {
    try {
      setUpdatingRequestId(requestId);

      const response = await apiFetch(tenantPath(`/identity/privileged-access/${requestId}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to update privileged-access request."
        );
      }

      await loadIdentityData(true);
    } catch (updateError) {
      console.error(
        "Privileged-access update error:",
        updateError
      );

      window.alert(updateError.message);
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const pamTransitionOptions = (status) => {
    const normalized = String(
      status || ""
    ).toLowerCase();

    const transitions = {
      requested: [
        {
          value: "requested",
          label: "Requested",
        },
        {
          value: "under_review",
          label: "Move to Under Review",
        },
        {
          value: "rejected",
          label: "Reject",
        },
      ],

      under_review: [
        {
          value: "under_review",
          label: "Under Review",
        },
        {
          value: "approved",
          label: "Approve",
        },
        {
          value: "rejected",
          label: "Reject",
        },
      ],

      approved: [
        {
          value: "approved",
          label: "Approved",
        },
        {
          value: "active",
          label: "Activate",
        },
        {
          value: "revoked",
          label: "Revoke",
        },
        {
          value: "expired",
          label: "Mark Expired",
        },
      ],

      active: [
        {
          value: "active",
          label: "Active",
        },
        {
          value: "revoked",
          label: "Revoke",
        },
        {
          value: "expired",
          label: "Mark Expired",
        },
      ],

      rejected: [
        {
          value: "rejected",
          label: "Rejected",
        },
      ],

      revoked: [
        {
          value: "revoked",
          label: "Revoked",
        },
      ],

      expired: [
        {
          value: "expired",
          label: "Expired",
        },
      ],
    };

    return (
      transitions[normalized] || [
        {
          value: normalized,
          label: formatLabel(normalized),
        },
      ]
    );
  };

  const identityMetrics =
    summary?.identity_metrics || {};

  const privilegedMetrics =
    summary?.privileged_metrics || {};

  const postureMetrics =
    summary?.posture_metrics || {};

  const decisionMetrics =
    summary?.decision_metrics || {};

  const mfaCoverage = useMemo(() => {
    const total = Number(
      identityMetrics.total_identities || 0
    );

    const enabled = Number(
      identityMetrics.mfa_enabled_identities || 0
    );

    if (!total) return 0;

    return enabled / total * 100;
  }, [identityMetrics]);

  if (loading) {
    return (
      <div className="identity-loading">
        Loading Identity and Zero Trust...
      </div>
    );
  }

  return (
    <div className="identity-page">
      <section className="identity-header">
        <div>
          <div className="identity-eyebrow">
            IDENTITY, PAM & ZERO TRUST
          </div>

          <h2>Identity Security Center</h2>

          <p>
            Govern workforce identities, privileged access,
            role assignments, device posture, and risk-based
            access decisions.
          </p>
        </div>

        <button
          type="button"
          onClick={() => loadIdentityData(true)}
          disabled={refreshing}
        >
          {refreshing
            ? "Refreshing..."
            : "Refresh Identity"}
        </button>
      </section>

      {error && (
        <div className="identity-warning">
          Identity refresh failed: {error}
        </div>
      )}

      <section className="identity-metric-grid">
        <article>
          <span>Total Identities</span>
          <strong>
            {identityMetrics.total_identities || 0}
          </strong>
          <small>
            {identityMetrics.active_identities || 0}
            {" "}active
          </small>
        </article>

        <article className="identity-privileged-card">
          <span>Privileged Identities</span>
          <strong>
            {identityMetrics.privileged_identities || 0}
          </strong>
        </article>

        <article>
          <span>MFA Coverage</span>
          <strong>
            {mfaCoverage.toFixed(1)}%
          </strong>
        </article>

        <article className="identity-risk-card">
          <span>Elevated-Risk Identities</span>
          <strong>
            {identityMetrics.elevated_risk_identities || 0}
          </strong>
        </article>

        <article>
          <span>Active PAM Sessions</span>
          <strong>
            {privilegedMetrics.active_privileged_sessions || 0}
          </strong>
        </article>

        <article>
          <span>Average Device Posture</span>
          <strong>
            {Number(
              postureMetrics.average_posture_score || 0
            ).toFixed(1)}
            %
          </strong>
        </article>
      </section>

      <section className="identity-grid">
        <article className="identity-panel">
          <div className="identity-panel-heading">
            <div>
              <span>ZERO TRUST DECISIONS</span>
              <h3>Access Outcomes</h3>
            </div>
          </div>

          <div className="identity-decision-grid">
            <div>
              <span>Allowed</span>
              <strong>
                {decisionMetrics.allowed || 0}
              </strong>
            </div>

            <div>
              <span>Conditional</span>
              <strong>
                {decisionMetrics.conditional || 0}
              </strong>
            </div>

            <div>
              <span>Challenged</span>
              <strong>
                {decisionMetrics.challenged || 0}
              </strong>
            </div>

            <div>
              <span>Denied</span>
              <strong>
                {decisionMetrics.denied || 0}
              </strong>
            </div>
          </div>
        </article>

        <article className="identity-panel">
          <div className="identity-panel-heading">
            <div>
              <span>DEVICE TRUST</span>
              <h3>Posture Health</h3>
            </div>
          </div>

          <div className="identity-posture-grid">
            <div>
              <span>Evaluated</span>
              <strong>
                {postureMetrics.evaluated_devices || 0}
              </strong>
            </div>

            <div>
              <span>Compliant</span>
              <strong>
                {postureMetrics.compliant_devices || 0}
              </strong>
            </div>

            <div>
              <span>High Risk</span>
              <strong>
                {postureMetrics.high_risk_devices || 0}
              </strong>
            </div>
          </div>
        </article>
      </section>

            <section className="identity-panel">
        <div className="identity-panel-heading">
          <div>
            <span>PRIVILEGED ACCESS ADMINISTRATION</span>
            <h3>Create Privileged Access Request</h3>
          </div>
        </div>

        <p>
          Request time-limited privileged access for an
          active, MFA-enabled identity. Role eligibility and
          maximum session duration are enforced by the backend.
        </p>

        <div className="identity-review-summary">
          <label>
            Identity
            <select
              value={pamCreateForm.identity_id}
              onChange={(event) =>
                setPamCreateForm((current) => ({
                  ...current,
                  identity_id:
                    event.target.value,
                }))
              }
            >
              <option value="">
                Select identity
              </option>

              {identities
                .filter(
                  (identity) =>
                    String(
                      identity.status || ""
                    ).toLowerCase() === "active" &&
                    Boolean(
                      identity.mfa_enabled
                    )
                )
                .map((identity) => (
                  <option
                    key={identity.id}
                    value={identity.id}
                  >
                    {identity.display_name ||
                      identity.username}
                    {" · "}
                    {identity.username}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Privileged Role
            <select
              value={pamCreateForm.role_id}
              onChange={(event) => {
                const nextRoleId =
                  event.target.value;

                const nextRole = roles.find(
                  (role) =>
                    String(role.id) ===
                    String(nextRoleId)
                );

                const maximum =
                  nextRole?.maximum_session_minutes == null
                    ? null
                    : Number(
                        nextRole.maximum_session_minutes
                      );

                setPamCreateForm(
                  (current) => ({
                    ...current,
                    role_id: nextRoleId,
                    requested_duration_minutes:
                      maximum !== null &&
                      Number.isFinite(maximum)
                        ? String(
                            Math.min(
                              60,
                              maximum
                            )
                          )
                        : current.requested_duration_minutes,
                  })
                );
              }}
            >
              <option value="">
                Select privileged role
              </option>

              {roles
                .filter(
                  (role) =>
                    Boolean(role.privileged) &&
                    role.enabled !== false
                )
                .map((role) => (
                  <option
                    key={role.id}
                    value={role.id}
                  >
                    {role.role_name}
                    {role.maximum_session_minutes
                      ? ` · max ${role.maximum_session_minutes} min`
                      : ""}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Requested Duration (minutes)
            <input
              type="number"
              min="5"
              value={
                pamCreateForm.requested_duration_minutes
              }
              onChange={(event) =>
                setPamCreateForm((current) => ({
                  ...current,
                  requested_duration_minutes:
                    event.target.value,
                }))
              }
            />
          </label>

          <label>
            Resource Type
            <input
              type="text"
              value={pamCreateForm.resource_type}
              placeholder="Optional"
              onChange={(event) =>
                setPamCreateForm((current) => ({
                  ...current,
                  resource_type:
                    event.target.value,
                }))
              }
            />
          </label>

          <label>
            Resource Identifier
            <input
              type="text"
              value={
                pamCreateForm.resource_identifier
              }
              placeholder="Optional"
              onChange={(event) =>
                setPamCreateForm((current) => ({
                  ...current,
                  resource_identifier:
                    event.target.value,
                }))
              }
            />
          </label>
        </div>

        <label>
          Business Justification
          <textarea
            value={
              pamCreateForm.business_justification
            }
            placeholder="Explain why privileged access is required."
            onChange={(event) =>
              setPamCreateForm((current) => ({
                ...current,
                business_justification:
                  event.target.value,
              }))
            }
          />
        </label>

        <button
          type="button"
          onClick={
            createPrivilegedAccessRequest
          }
          disabled={creatingPamRequest}
        >
          {creatingPamRequest
            ? "Submitting Request..."
            : "Submit Privileged Access Request"}
        </button>

        {pamCreateResult && (
          <p className="identity-cell-subtext">
            Request{" "}
            {pamCreateResult.request_number ||
              pamCreateResult.id}{" "}
            created with status{" "}
            {pamCreateResult.status}.
          </p>
        )}
      </section>

<section className="identity-panel">
        <div className="identity-panel-heading">
          <div>
            <span>PRIVILEGED ACCESS MANAGEMENT</span>
            <h3>Access Request Queue</h3>
          </div>
        </div>

        <div className="identity-table-wrapper">
          <table className="identity-table">
            <thead>
              <tr>
                <th>Request</th>
                <th>Identity</th>
                <th>Role</th>
                <th>Resource</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Decision</th>
              </tr>
            </thead>

            <tbody>
              {requests.length > 0 ? (
                requests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong>
                        {request.request_number}
                      </strong>
                    </td>

                    <td>
                      {request.display_name ||
                        request.username}

                      <span className="identity-cell-subtext">
                        {request.email || request.username}
                      </span>
                    </td>

                    <td>
                      {request.role_name || "Custom Access"}
                    </td>

                    <td>
                      {formatLabel(
                        request.resource_type
                      )}

                      <span className="identity-cell-subtext">
                        {request.resource_identifier}
                      </span>
                    </td>

                    <td>
                      {request.requested_duration_minutes}
                      {" "}minutes
                    </td>

                    <td>
                      <span
                        className={decisionClass(
                          request.status
                        )}
                      >
                        {formatLabel(request.status)}
                      </span>
                    </td>

                    <td>
                      {formatDate(
                        request.access_expires_at
                      )}
                    </td>

                    <td>
                      <select
                        value={request.status}
                        disabled={
                          updatingRequestId === request.id ||
                          pamTransitionOptions(
                            request.status
                          ).length <= 1
                        }
                        onChange={(event) =>
                          updateRequest(
                            request.id,
                            event.target.value
                          )
                        }
                      >
                        {pamTransitionOptions(
                          request.status
                        ).map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="identity-empty"
                  >
                    No privileged-access requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

              <section className="identity-panel">
          <div className="identity-panel-heading">
            <div>
              <span>IDENTITY ADMINISTRATION</span>
              <h3>Create Identity</h3>
            </div>
          </div>

          <p>
            Create a workforce or service identity inside the
            currently selected tenant. Audit attribution is
            derived by the backend from the authenticated
            administrator.
          </p>

          <div className="identity-review-summary">
            <label>
              Username
              <input
                type="text"
                value={identityCreateForm.username}
                placeholder="e.g. p2b-test-user"
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    username: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={identityCreateForm.display_name}
                placeholder="Display name"
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    display_name: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Email
              <input
                type="email"
                value={identityCreateForm.email}
                placeholder="Optional"
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Identity Type
              <select
                value={identityCreateForm.identity_type}
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    identity_type: event.target.value,
                  }))
                }
              >
                <option value="workforce">
                  Workforce
                </option>

                <option value="service">
                  Service Account
                </option>
              </select>
            </label>

            <label>
              Status
              <select
                value={identityCreateForm.status}
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
              >
                <option value="active">
                  Active
                </option>

                <option value="disabled">
                  Disabled
                </option>

                <option value="suspended">
                  Suspended
                </option>
              </select>
            </label>
          </div>

          <div className="identity-review-summary">
            <label>
              <input
                type="checkbox"
                checked={identityCreateForm.mfa_enabled}
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    mfa_enabled: event.target.checked,
                  }))
                }
              />
              MFA Enabled
            </label>

            <label>
              <input
                type="checkbox"
                checked={
                  identityCreateForm.passwordless_enabled
                }
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    passwordless_enabled:
                      event.target.checked,
                  }))
                }
              />
              Passwordless
            </label>

            <label>
              <input
                type="checkbox"
                checked={identityCreateForm.service_account}
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    service_account: event.target.checked,
                  }))
                }
              />
              Service Account
            </label>

            <label>
              <input
                type="checkbox"
                checked={identityCreateForm.privileged}
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    privileged: event.target.checked,
                  }))
                }
              />
              Privileged
            </label>

            <label>
              <input
                type="checkbox"
                checked={identityCreateForm.break_glass}
                onChange={(event) =>
                  setIdentityCreateForm((current) => ({
                    ...current,
                    break_glass: event.target.checked,
                  }))
                }
              />
              Break Glass
            </label>
          </div>

          <button
            type="button"
            onClick={createIdentity}
            disabled={creatingIdentity}
          >
            {creatingIdentity
              ? "Creating Identity..."
              : "Create Identity"}
          </button>

          {identityCreateResult && (
            <p className="identity-cell-subtext">
              Identity created successfully:
              {" "}
              {identityCreateResult.display_name ||
                identityCreateResult.username}
            </p>
          )}
        </section>

<section className="identity-panel">
        <div className="identity-panel-heading">
          <div>
            <span>IDENTITY INVENTORY</span>
            <h3>Workforce and Service Accounts</h3>
          </div>
        </div>

        <div className="identity-table-wrapper">
          <table className="identity-table">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Type</th>
                <th>Status</th>
                <th>Privileged</th>
                <th>MFA</th>
                <th>Risk</th>
                <th>Roles</th>
                <th>Department</th>
              </tr>
            </thead>

            <tbody>
              {identities.length > 0 ? (
                identities.map((identity) => (
                  <tr key={identity.id}>
                    <td>
                      <strong>
                        {identity.display_name ||
                          identity.username}
                      </strong>

                      <span className="identity-cell-subtext">
                        {identity.email ||
                          identity.username}
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        identity.identity_type
                      )}
                    </td>

                    <td>
                      {formatLabel(identity.status)}
                    </td>

                    <td>
                      {identity.privileged
                        ? "Yes"
                        : "No"}
                    </td>

                    <td>
                      {identity.mfa_enabled
                        ? "Enabled"
                        : "Disabled"}
                    </td>

                    <td>
                      <span
                        className={riskClass(
                          identity.risk_level
                        )}
                      >
                        {Number(
                          identity.risk_score || 0
                        ).toFixed(1)}
                        {" · "}
                        {formatLabel(
                          identity.risk_level
                        )}
                      </span>
                    </td>

                    <td>
                      {identity.active_role_count || 0}
                    </td>

                    <td>
                      {identity.department || "N/A"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="identity-empty"
                  >
                    No identity accounts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {postures.length > 0 && (
        <section className="identity-panel">
          <div className="identity-panel-heading">
            <div>
              <span>MANAGED DEVICE POSTURE</span>
              <h3>Trusted Endpoint Inventory</h3>
            </div>
          </div>

          <div className="identity-table-wrapper">
            <table className="identity-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Posture Score</th>
                  <th>Status</th>
                  <th>Recommendation</th>
                  <th>Agent</th>
                  <th>Firewall</th>
                  <th>Antivirus</th>
                  <th>Encrypted</th>
                </tr>
              </thead>

              <tbody>
                {postures.map((posture) => (
                  <tr key={posture.id}>
                    <td>
                      <strong>{posture.hostname}</strong>

                      <span className="identity-cell-subtext">
                        {posture.device_uuid}
                      </span>
                    </td>

                    <td>
                      {Number(
                        posture.posture_score ?? 0
                      ).toFixed(1)}
                      %

                      <span className="identity-cell-subtext">
                        Evidence Coverage{" "}
                        {postureEvidenceCoverage(
                          posture
                        ).toFixed(1)}
                        %
                      </span>
                    </td>

                    <td>
                      <span
                        className={postureClass(
                          posture.posture_status
                        )}
                      >
                        {postureAssessmentLabel(
                          posture
                        )}
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        posture.access_recommendation
                      )}
                    </td>

                    <td>
                      {postureSignalLabel(
                        posture,
                        "agent_healthy",
                        "Healthy",
                        "Unhealthy"
                      )}
                    </td>

                    <td>
                      {postureSignalLabel(
                        posture,
                        "firewall_enabled",
                        "Enabled",
                        "Disabled"
                      )}
                    </td>

                    <td>
                      {postureSignalLabel(
                        posture,
                        "antivirus_enabled",
                        "Enabled",
                        "Disabled"
                      )}
                    </td>

                    <td>
                      {postureSignalLabel(
                        posture,
                        "disk_encrypted",
                        "Encrypted",
                        "Not Encrypted"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {decisions.length > 0 && (
        <section className="identity-panel">
          <div className="identity-panel-heading">
            <div>
              <span>POLICY DECISION HISTORY</span>
              <h3>Recent Zero Trust Decisions</h3>
            </div>
          </div>

          <div className="identity-table-wrapper">
            <table className="identity-table">
              <thead>
                <tr>
                  <th>Decision</th>
                  <th>Resource</th>
                  <th>Action</th>
                  <th>Identity Risk</th>
                  <th>Device Score</th>
                  <th>Overall Risk</th>
                  <th>Reason</th>
                  <th>Evaluated</th>
                </tr>
              </thead>

              <tbody>
                {decisions.map((decision) => (
                  <tr key={decision.id}>
                    <td>
                      <span
                        className={decisionClass(
                          decision.decision
                        )}
                      >
                        {formatLabel(
                          decision.decision
                        )}
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        decision.resource_type
                      )}

                      <span className="identity-cell-subtext">
                        {decision.resource_identifier}
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        decision.requested_action
                      )}
                    </td>

                    <td>
                      {decision.identity_risk_score}
                    </td>

                    <td>
                      {decision.device_posture_score}
                    </td>

                    <td>
                      {decision.overall_risk_score}
                    </td>

                    <td>
                      {decision.decision_reason}
                    </td>

                    <td>
                      {formatDate(
                        decision.evaluated_at
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="identity-panel">
        <div className="identity-panel-heading">
          <div>
            <span>IDENTITY GOVERNANCE</span>
            <h3>Access Reviews</h3>
          </div>
        </div>

        <div className="identity-table-wrapper">
          <table className="identity-table">
            <thead>
              <tr>
                <th>Review</th>
                <th>Type</th>
                <th>Status</th>
                <th>Total</th>
                <th>Approved</th>
                <th>Revoked</th>
                <th>Pending</th>
                <th>Reviewer</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {accessReviews.length > 0 ? (
                accessReviews.map((review) => (
                  <tr key={review.id}>
                    <td>
                      <strong>{review.review_name}</strong>

                      <span className="identity-cell-subtext">
                        {review.scope_description}
                      </span>
                    </td>

                    <td>
                      {formatLabel(review.review_type)}
                    </td>

                    <td>
                      <span
                        className={decisionClass(
                          review.status
                        )}
                      >
                        {formatLabel(review.status)}
                      </span>
                    </td>

                    <td>{review.total_assignments || 0}</td>
                    <td>{review.approved_assignments || 0}</td>
                    <td>{review.revoked_assignments || 0}</td>
                    <td>{review.pending_assignments || 0}</td>
                    <td>{review.reviewer_name || "Unassigned"}</td>

                    <td>
                      <button
                        type="button"
                        className="identity-review-button"
                        onClick={() =>
                          openAccessReview(review.id)
                        }
                      >
                        Review Access
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="identity-empty"
                  >
                    No access reviews found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedReview && (
        <section className="identity-panel">
          <div className="identity-panel-heading identity-review-heading">
            <div>
              <span>ACCESS CERTIFICATION</span>
              <h3>{selectedReview.review_name}</h3>
            </div>

            <button
              type="button"
              className="identity-review-close"
              onClick={() => setSelectedReview(null)}
            >
              Close
            </button>
          </div>

          <div className="identity-review-summary">
            <span>
              Total
              <strong>
                {selectedReview.total_assignments || 0}
              </strong>
            </span>

            <span>
              Approved
              <strong>
                {selectedReview.approved_assignments || 0}
              </strong>
            </span>

            <span>
              Revoked
              <strong>
                {selectedReview.revoked_assignments || 0}
              </strong>
            </span>

            <span>
              Pending
              <strong>
                {selectedReview.pending_assignments || 0}
              </strong>
            </span>
          </div>

          <div className="identity-table-wrapper">
            <table className="identity-table">
              <thead>
                <tr>
                  <th>Identity</th>
                  <th>Role</th>
                  <th>Privileged</th>
                  <th>Identity Risk</th>
                  <th>Assignment</th>
                  <th>Expires</th>
                  <th>Decision</th>
                  <th>Review Action</th>
                </tr>
              </thead>

              <tbody>
                {(selectedReview.items || []).map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>
                        {item.display_name || item.username}
                      </strong>

                      <span className="identity-cell-subtext">
                        {item.email || item.username}
                      </span>
                    </td>

                    <td>
                      {item.role_name || "Unknown role"}

                      <span className="identity-cell-subtext">
                        {item.role_key}
                      </span>
                    </td>

                    <td>
                      {item.role_privileged ? "Yes" : "No"}
                    </td>

                    <td>
                      <span
                        className={riskClass(
                          item.risk_level
                        )}
                      >
                        {Number(
                          item.risk_score || 0
                        ).toFixed(1)}
                        {" · "}
                        {formatLabel(item.risk_level)}
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        item.assignment_type
                      )}
                    </td>

                    <td>{formatDate(item.expires_at)}</td>

                    <td>
                      <span
                        className={decisionClass(
                          item.decision
                        )}
                      >
                        {formatLabel(item.decision)}
                      </span>
                    </td>

                    <td>
                      <div className="identity-review-actions">
                        <button
                          type="button"
                          disabled={
                            updatingReviewItemId === item.id
                          }
                          onClick={() =>
                            updateAccessReviewItem(
                              selectedReview.id,
                              item.id,
                              "approved"
                            )
                          }
                        >
                          Approve
                        </button>

                        <button
                          type="button"
                          className="identity-review-revoke"
                          disabled={
                            updatingReviewItemId === item.id
                          }
                          onClick={() =>
                            updateAccessReviewItem(
                              selectedReview.id,
                              item.id,
                              "revoked"
                            )
                          }
                        >
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

              <section className="identity-panel">
          <div className="identity-panel-heading">
            <div>
              <span>ROLE ADMINISTRATION</span>
              <h3>Assign Identity Role</h3>
            </div>
          </div>

          <p>
            Assign an available tenant or global role to an
            identity in the currently selected company.
            Administrator attribution is derived by the backend.
          </p>

          <div className="identity-review-summary">
            <label>
              Identity
              <select
                value={roleAssignmentForm.identity_id}
                onChange={(event) =>
                  setRoleAssignmentForm((current) => ({
                    ...current,
                    identity_id: event.target.value,
                  }))
                }
              >
                <option value="">
                  Select identity
                </option>

                {identities.map((identity) => (
                  <option
                    key={identity.id}
                    value={identity.id}
                  >
                    {identity.display_name ||
                      identity.username}
                    {" · "}
                    {identity.username}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Role
              <select
                value={roleAssignmentForm.role_id}
                onChange={(event) =>
                  setRoleAssignmentForm((current) => ({
                    ...current,
                    role_id: event.target.value,
                  }))
                }
              >
                <option value="">
                  Select role
                </option>

                {roles.map((role) => (
                  <option
                    key={role.id}
                    value={role.id}
                  >
                    {role.role_name}
                    {" · "}
                    {role.privileged
                      ? "Privileged"
                      : "Standard"}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Assignment Type
              <select
                value={roleAssignmentForm.assignment_type}
                onChange={(event) =>
                  setRoleAssignmentForm((current) => ({
                    ...current,
                    assignment_type: event.target.value,
                  }))
                }
              >
                <option value="permanent">
                  Permanent
                </option>

                <option value="temporary">
                  Temporary
                </option>
              </select>
            </label>
          </div>

          <button
            type="button"
            onClick={assignIdentityRole}
            disabled={assigningRole}
          >
            {assigningRole
              ? "Assigning Role..."
              : "Assign Role"}
          </button>

          {roleAssignmentResult && (
            <p className="identity-cell-subtext">
              Role assignment completed successfully.
            </p>
          )}
        </section>

<section className="identity-panel">
        <div className="identity-panel-heading">
          <div>
            <span>ROLE CATALOG</span>
            <h3>Enterprise Roles</h3>
          </div>
        </div>

        <div className="identity-role-grid">
          {roles.map((role) => (
            <article key={role.id}>
              <strong>{role.role_name}</strong>
              <span>{role.role_key}</span>
              <small>
                {role.privileged
                  ? "Privileged role"
                  : "Standard role"}
                {" · "}
                {role.active_assignment_count || 0}
                {" "}assignments
              </small>
            </article>
          ))}
        </div>
      </section>
      <section className="identity-panel identity-operational-evaluation">
        <div className="identity-panel-heading">
          <div>
            <span>ZERO TRUST EVALUATION</span>
            <h3>Evaluate Managed Device Posture</h3>
          </div>
        </div>

        <p>
          Evaluate a managed endpoint against the current
          Wireless Press posture engine. The result is calculated
          from platform evidence; it is not a manual Pass/Fail
          override.
        </p>

        <div className="identity-review-summary">
          <label>
            Managed Device UUID
            <input
              type="text"
              value={postureDeviceUuid}
              placeholder="Enter device UUID"
              onChange={(event) =>
                setPostureDeviceUuid(
                  event.target.value
                )
              }
            />
          </label>

          <button
            type="button"
            onClick={evaluateManagedDevicePosture}
            disabled={evaluatingPosture}
          >
            {evaluatingPosture
              ? "Evaluating..."
              : "Evaluate Posture"}
          </button>
        </div>

        {postures.length > 0 && (
          <div className="identity-review-summary">
            {postures.map((posture) => (
              <button
                type="button"
                key={posture.id}
                onClick={() =>
                  setPostureDeviceUuid(
                    posture.device_uuid
                  )
                }
              >
                Use {posture.hostname || posture.device_uuid}
              </button>
            ))}
          </div>
        )}

        {postureResult && (
          <div className="identity-posture-grid">
            <article>
              <span>
                Evidence-backed Score
              </span>

              <strong>
                {Number(
                  postureResult.posture_score ?? 0
                ).toFixed(1)}
                %
              </strong>
            </article>

            <article>
              <span>
                Evidence Coverage
              </span>

              <strong>
                {postureEvidenceCoverage(
                  postureResult
                ).toFixed(1)}
                %
              </strong>
            </article>

            <article>
              <span>
                Unknown Evidence
              </span>

              <strong>
                {unknownPostureSignalCount(
                  postureResult
                )}
              </strong>
            </article>

            <article>
              <span>
                Assessment State
              </span>

              <strong>
                {postureAssessmentLabel(
                  postureResult
                )}
              </strong>
            </article>

            <article>
              <span>Recommendation</span>
              <strong>
                {formatLabel(
                  postureResult.access_recommendation
                )}
              </strong>
            </article>

            <article>
              <span>Agent Healthy</span>
              <strong>
                {postureSignalLabel(
                  postureResult,
                  "agent_healthy",
                  "PASS",
                  "FAIL"
                )}
              </strong>
            </article>

            <article>
              <span>Firewall</span>
              <strong>
                {postureSignalLabel(
                  postureResult,
                  "firewall_enabled",
                  "PASS",
                  "FAIL"
                )}
              </strong>
            </article>

            <article>
              <span>Antivirus</span>
              <strong>
                {postureSignalLabel(
                  postureResult,
                  "antivirus_enabled",
                  "PASS",
                  "FAIL"
                )}
              </strong>
            </article>

            <article>
              <span>Disk Encryption</span>
              <strong>
                {postureSignalLabel(
                  postureResult,
                  "disk_encrypted",
                  "PASS",
                  "FAIL"
                )}
              </strong>
            </article>

            <article>
              <span>Critical Vulnerabilities</span>
              <strong>
                {postureNumericSignalLabel(
                  postureResult,
                  "critical_vulnerabilities",
                  postureResult.vulnerabilities_critical
                )}
              </strong>
            </article>

            <article>
              <span>High Vulnerabilities</span>
              <strong>
                {postureNumericSignalLabel(
                  postureResult,
                  "high_vulnerabilities",
                  postureResult.vulnerabilities_high
                )}
              </strong>
            </article>
          </div>
        )}
      </section>

      <section className="identity-panel">
        <div className="identity-panel-heading">
          <div>
            <span>POLICY DECISION POINT</span>
            <h3>Evaluate Access Decision</h3>
          </div>
        </div>

        <div className="identity-review-summary">
          <label>
            Identity
            <select
              value={accessDecisionForm.identity_id}
              onChange={(event) =>
                setAccessDecisionForm((current) => ({
                  ...current,
                  identity_id: event.target.value,
                }))
              }
            >
              <option value="">Select identity</option>

              {identities.map((identity) => (
                <option
                  key={identity.id}
                  value={identity.id}
                >
                  {identity.display_name ||
                    identity.username}
                </option>
              ))}
            </select>
          </label>

          <label>
            Device UUID
            <input
              type="text"
              value={accessDecisionForm.device_uuid}
              placeholder="Optional evaluated device"
              onChange={(event) =>
                setAccessDecisionForm((current) => ({
                  ...current,
                  device_uuid: event.target.value,
                }))
              }
            />
          </label>

          <label>
            Resource Type
            <input
              type="text"
              value={accessDecisionForm.resource_type}
              onChange={(event) =>
                setAccessDecisionForm((current) => ({
                  ...current,
                  resource_type: event.target.value,
                }))
              }
            />
          </label>

          <label>
            Resource
            <input
              type="text"
              value={
                accessDecisionForm.resource_identifier
              }
              placeholder="Optional resource identifier"
              onChange={(event) =>
                setAccessDecisionForm((current) => ({
                  ...current,
                  resource_identifier:
                    event.target.value,
                }))
              }
            />
          </label>

          <label>
            Requested Action
            <input
              type="text"
              value={accessDecisionForm.requested_action}
              onChange={(event) =>
                setAccessDecisionForm((current) => ({
                  ...current,
                  requested_action:
                    event.target.value,
                }))
              }
            />
          </label>

          <label>
            Session Risk
            <input
              type="number"
              min="0"
              max="100"
              value={
                accessDecisionForm.session_risk_score
              }
              onChange={(event) =>
                setAccessDecisionForm((current) => ({
                  ...current,
                  session_risk_score:
                    event.target.value,
                }))
              }
            />
          </label>
        </div>

        <button
          type="button"
          onClick={evaluateAccessDecision}
          disabled={evaluatingAccess}
        >
          {evaluatingAccess
            ? "Evaluating Access..."
            : "Evaluate Access"}
        </button>

        {accessDecisionResult && (
          <div className="identity-posture-grid">
            <article>
              <span>Decision</span>
              <strong>
                {formatLabel(
                  accessDecisionResult.decision
                )}
              </strong>
            </article>

            <article>
              <span>Overall Risk</span>
              <strong>
                {accessDecisionResult.overall_risk_score}
              </strong>
            </article>

            <article>
              <span>Identity Risk</span>
              <strong>
                {accessDecisionResult.identity_risk_score}
              </strong>
            </article>

            <article>
              <span>Device Posture</span>
              <strong>
                {accessDecisionResult.device_posture_score}
              </strong>
            </article>

            <article>
              <span>Reason</span>
              <strong>
                {accessDecisionResult.decision_reason}
              </strong>
            </article>
          </div>
        )}
      </section>

    </div>
  );
}

export default IdentityZeroTrustPage;
